
import { Cafe3DIcon } from '@/components/icons/3d/Cafe3DIcon';
import { Restaurant3DIcon } from '@/components/icons/3d/Restaurant3DIcon';
import { Lounge3DIcon } from '@/components/icons/3d/Lounge3DIcon';
import { Gym3DIcon } from '@/components/icons/3d/Gym3DIcon';
import { GameCenter3DIcon } from '@/components/icons/3d/GameCenter3DIcon';
import { School3DIcon } from '@/components/icons/3d/School3DIcon';
import { Billboard3DIcon } from '@/components/icons/3d/Billboard3DIcon';
import { BuildingScreen3DIcon } from '@/components/icons/3d/BuildingScreen3DIcon';
import { BridgeScreen3DIcon } from '@/components/icons/3d/BridgeScreen3DIcon';
import { MegacomScreen3DIcon } from '@/components/icons/3d/MegacomScreen3DIcon';
import { SuperMegaScreen3DIcon } from '@/components/icons/3d/SuperMegaScreen3DIcon';
import { Unipole3DIcon } from '@/components/icons/3d/Unipole3DIcon';
import { Gate3DIcon } from '@/components/icons/3d/Gate3DIcon';
import { Barbershop3DIcon } from '@/components/icons/3d/Barbershop3DIcon';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { useMediaOwnerCheck } from '@/hooks/useMediaOwnerCheck';

interface MapLegendProps {
  activeMediaKind?: string;
}

const indoorLocations = [
  { Icon: Cafe3DIcon, color: '#8B4513', label: 'Cafe', adminOnly: false },
  { Icon: Restaurant3DIcon, color: '#FF4500', label: 'Restaurant', adminOnly: false },
  { Icon: Lounge3DIcon, color: '#2478C8', label: 'Lounge', adminOnly: false },
  { Icon: Gym3DIcon, color: '#8B5CF6', label: 'Gym', adminOnly: false },
  { Icon: GameCenter3DIcon, color: '#F59E0B', label: 'Game Center', adminOnly: false },
  { Icon: School3DIcon, color: '#10B981', label: 'School', adminOnly: false },
  { Icon: Barbershop3DIcon, color: '#E91E63', label: 'Barbershop', adminOnly: false },
];

const outdoorLocations = [
  { Icon: Billboard3DIcon, color: '#BC1A6C', label: 'Billboard', adminOnly: false },
  { Icon: BuildingScreen3DIcon, color: '#BC1A6C', label: 'Building Screens', adminOnly: false },
  { Icon: BridgeScreen3DIcon, color: '#BC1A6C', label: 'Digital Bridge', adminOnly: false },
  { Icon: MegacomScreen3DIcon, color: '#BC1A6C', label: 'Megacom', adminOnly: false },
  { Icon: SuperMegaScreen3DIcon, color: '#BC1A6C', label: 'Super Mega', adminOnly: false },
  { Icon: Unipole3DIcon, color: '#0b7b41', label: 'Unipole', adminOnly: true, owner: 'Rotana Signs' },
  { Icon: Gate3DIcon, color: '#0b7b41', label: 'Smart Gate', adminOnly: true, owner: 'Rotana Signs' },
];

export const MapLegend = ({ activeMediaKind = 'all' }: MapLegendProps) => {
  const { isAdmin } = useAdminCheck();
  const { isMediaOwner, mediaOwnerName } = useMediaOwnerCheck();

  const canSeeAdminOnly = (entry: { adminOnly: boolean; owner?: string }) => {
    if (!entry.adminOnly) return true;
    if (isAdmin) return true;
    if (isMediaOwner && mediaOwnerName && entry.owner === mediaOwnerName) return true;
    return false;
  };

  const allLocations =
    activeMediaKind === 'indoor'
      ? indoorLocations
      : activeMediaKind === 'outdoor'
        ? outdoorLocations
        : [...indoorLocations, ...outdoorLocations];

  const visibleLocations = allLocations.filter(canSeeAdminOnly);

  return (
    <div className="w-full bg-card border-b border-border/50 px-4 py-2">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs font-medium text-muted-foreground">
          {activeMediaKind === 'indoor' && 'Indoor'}
          {activeMediaKind === 'outdoor' && 'Outdoor'}
          {activeMediaKind === 'all' && 'Legend'}:
        </span>
        {visibleLocations.map((location, index) => (
          <div key={index} className="flex items-center gap-1 whitespace-nowrap">
            <location.Icon size={16} color={location.color} />
            <span className="text-xs text-muted-foreground">{location.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
